import type { Brand } from './brand';

/**
 * The FRONTEND-CONDITION axis — conditions the frontend is in.
 *
 * THESE ARE NOT CONTRACT STATES. `provenance.ts` records this as a
 * `FrontendOnlyDerivation` with `frozenPath: null` and `blob: null`, so a
 * non-derivation cannot be mistaken for a derivation. No frozen artifact defines
 * any of these, because the contract does not have them.
 *
 * The brand is deliberately different from `PresenceAxis` and `FreshnessAxis`.
 * That is the structural guarantee behind FE-1 Stage Work Item §7.5:
 *
 *     "Loading and Empty are not contract states and MUST NOT be substituted
 *      for UNKNOWN."
 *
 * Substituting one is a compile error. `./invariants.ts` asserts that this axis
 * is disjoint from the presence axis.
 *
 *   LOADING  No contract outcome has been obtained yet. It is a statement about
 *            the frontend's own progress, NOT a statement about the subject. An
 *            observation in this condition carries NO presence and NO freshness —
 *            `./observation.ts` omits both fields, so code physically cannot read
 *            a presence off a loading observation.
 *
 *   EMPTY    A resolved projection contained zero items. USE WITH EXTREME CARE:
 *            on the Capability Definition read plane an EMPTY SOURCE maps to
 *            `UNKNOWN`, never to empty (FE-1 Stage Work Item §6.5). This
 *            condition must therefore NEVER be produced from that plane's
 *            outcome. Producing it there is a defect, not a simplification.
 *
 *   ERROR    A frontend failure — a render fault, a wiring fault, a contract
 *            violation caught in code. It is NEVER a contract outcome: the frozen
 *            contract has no error status, and a reader failure is reported by
 *            the contract as `UNAVAILABLE`. ERROR must never absorb UNKNOWN and
 *            must never be rendered as a data absence.
 */

/** No contract outcome obtained yet. Says nothing about the subject. */
export type ConditionLoading = Brand<'LOADING', 'FrontendConditionAxis'>;

/** A resolved projection contained zero items. NEVER a substitute for UNKNOWN. */
export type ConditionEmpty = Brand<'EMPTY', 'FrontendConditionAxis'>;

/** A frontend engineering failure. NEVER a contract outcome. */
export type ConditionError = Brand<'ERROR', 'FrontendConditionAxis'>;

/** The closed frontend-condition vocabulary. */
export type FrontendCondition = ConditionLoading | ConditionEmpty | ConditionError;

/**
 * The three frontend conditions. The only construction site for the vocabulary.
 */
export const FrontendCondition = Object.freeze({
  LOADING: 'LOADING' as ConditionLoading,
  EMPTY: 'EMPTY' as ConditionEmpty,
  ERROR: 'ERROR' as ConditionError,
});
